import type { FC } from 'react'
import React, { useReducer, useState, useEffect } from 'react'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { cloneDeep } from 'lodash'
import {
  useGetCompanyUserQuery,
  useGetCompanyUsersMatrixQuery,
  useCreateCompanyUserMutation,
  useUpdateCompanyUserMutation,
  useUpdateCompanyUserPermissionsMutation,
} from '@homeviews/store'
import { mixpanelTrack } from '@homeviews/seo'
import { getFormChanges } from '@homeviews/utils'
import {
  Typography,
  Card,
  Button,
  Stepper,
  Snackbar,
  Spinner,
} from '@homeviews/shared-ui'

import EditUserForm from '../EditUserForm'
import UserSummary from '../UserSummary'
import PermissionsTable from '../PermissionsTable'
import type { User } from '@homeviews/models'

const USER_STEPS = [
  {
    step: 1,
    label: 'Add User',
  },
  {
    step: 2,
    label: 'Set Permissions',
  },
  {
    step: 3,
    label: 'Confirmation',
  },
]

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
      '& h1': {
        marginBottom: 0,
      },
    },
    backButton: {
      marginRight: 6,
    },
    stepperWrapper: {},
    buttonsWrapper: {
      display: 'flex',
    },
  }),
)

const NOTIFICATIONS_MAP = {
  all_reviews: 'ALL',
  positive_reviews: 'FOUR_TO_FIVE',
  negative_reviews: 'ONE_TO_THREE',
}

interface IState {
  step: number
  user: Partial<User>
  isEditForm: boolean
}

const initialState: IState = {
  step: 1,
  user: null,
  isEditForm: false,
}

const UserManager: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { id: userID } = useParams<{ id: string }>()

  const { data: matrixResponse, isFetching: isFetchingMatrix } =
    useGetCompanyUsersMatrixQuery(null, {
      skip: !!userID,
    })

  const {
    data: initialData,
    error,
    isFetching,
  } = useGetCompanyUserQuery(
    { id: userID ? parseInt(userID) : null },
    {
      skip: !userID,
    },
  )

  const [state, setState] = useReducer(
    (state: IState, newState: Partial<IState>) => ({ ...state, ...newState }),
    initialState,
  )

  const [snackbarVisible, setSnackbarVisible] = useState<{
    open: boolean
    message: string
    status?: 'success' | 'error'
  }>({
    open: false,
    message: '',
    status: undefined,
  })

  useEffect(() => {
    if (initialData) {
      setState({
        ...state,
        user: initialData,
        step: 2,
        isEditForm: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, location?.pathname])

  useEffect(() => {
    if (matrixResponse) {
      setState({
        ...state,
        user: {
          ...state.user,
          permissions: matrixResponse,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixResponse])

  const [updateUser, { isLoading: isUpdating, error: isUpdatingError }] =
    useUpdateCompanyUserMutation()

  const [
    updateUserPermissions,
    {
      isLoading: isUpdatingUserPermissions,
      error: isUpdatingUserPermissionsError,
    },
  ] = useUpdateCompanyUserPermissionsMutation()

  const [createUser, { isLoading: isCreating, error: isCreatingError }] =
    useCreateCompanyUserMutation()

  const handleNextStepClick = () => {
    setState({
      ...state,
      step: state.step + 1,
    })
  }

  const handlePrevStepClick = () => {
    if (state.isEditForm) {
      history.push('/users')
      return
    }
    const newStep = state.step - 1
    setState({
      ...state,
      step: newStep < 1 ? 1 : newStep,
    })
  }

  const handleBackToUsersClick = () => {
    history.push('/users')
  }

  const createOrUpdateUser = async () => {
    const global_permissions = []
    const development_permissions = {}

    if (state.user?.permissions?.EDIT_COMPANY_PAGE) {
      global_permissions.push('EDIT_COMPANY_PAGE')
    }

    if (state.user?.permissions?.EDIT_PERMISSIONS) {
      global_permissions.push('EDIT_PERMISSIONS')
    }

    if (state.user?.permissions?.VIEW_MARKETING_MATERIALS) {
      global_permissions.push('VIEW_MARKETING_MATERIALS')
    }

    if (state.user?.permissions?.CREATE_WIDGETS) {
      global_permissions.push('CREATE_WIDGETS')
    }

    const newPermissions = state?.user?.permissions
      ? cloneDeep(state.user.permissions)
      : {}

    for (const devId in newPermissions.developments) {
      const dev = newPermissions.developments[devId]

      const permissionsKeys = Object.keys(dev.permissions)
      const preparedPermissions = permissionsKeys.filter((key) => {
        return dev.permissions[key]
      })

      const notificationsKeys = Object.keys(dev.review_notifications)
      let preparedNotifications = notificationsKeys.filter((key) => {
        return dev.review_notifications[key]
      })

      if (preparedNotifications.length) {
        preparedNotifications = preparedNotifications.map(
          (notification) => NOTIFICATIONS_MAP[notification],
        )
      }

      development_permissions[devId] = {
        permissions: preparedPermissions,
        review_notifications: preparedNotifications,
      }
    }

    const payload = {
      first_name: state.user?.first_name,
      last_name: state.user?.last_name,
      email: state.user?.email,
      job_title: state.user?.job_title,
      global_permissions,
      development_permissions,
    }

    const labelMap = {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      job_title: 'Job Title',
      global_permissions: 'Global Permissions',
      development_permissions: 'Development Permissions',
    }

    const params: any = {
      data: payload,
    }

    if (state.isEditForm) {
      params.id = parseInt(userID)
    }

    const formChanges = getFormChanges(initialData, payload, labelMap, [
      'global_permissions',
      'development_permissions',
    ])

    let respData = null
    try {
      if (state.isEditForm && state.step === 2) {
        respData = await updateUserPermissions(params).unwrap()

        mixpanelTrack('USERS_PERMISSIONS_SET', {
          'User Name': state.user?.first_name || state.user?.last_name,
        })
      } else {
        if (!state.isEditForm) {
          mixpanelTrack('COMPANY_USERS_CREATE', {
            'User Name': state.user?.first_name || state.user?.last_name,
          })
        }

        respData = await (state.isEditForm
          ? updateUser(params).unwrap()
          : createUser(params).unwrap())
      }

      setState({
        ...state,
        step: state.isEditForm ? 2 : 3,
      })

      setSnackbarVisible({
        open: true,
        message: state.isEditForm ? 'User updated' : 'User created',
      })
    } catch (err) {
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
    }
  }

  const setPermissions = (permissions) => {
    setState({
      ...state,
      user: {
        ...state.user,
        permissions,
      },
    })
  }

  const handelCreateOrEditForm = (data: Partial<User>) => {
    setState({
      ...state,
      step: 2,
      user: {
        ...state.user,
        ...data,
      },
    })
  }

  const handleSnackbarClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackbarVisible({
      open: false,
      message: snackbarVisible?.message,
      status: snackbarVisible?.status,
    })
  }

  if (isFetching || isUpdating || isUpdatingUserPermissions) {
    return <Spinner />
  }

  if (state.step === 1) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h1">Add User</Typography>
          <div className={classes.stepperWrapper}>
            <Stepper steps={USER_STEPS} currentStep={state?.step} />
          </div>
        </div>
        <EditUserForm newUserForm onSubmitCallback={handelCreateOrEditForm} />
        <div>&nbsp;</div>
      </div>
    )
  }

  if (state.step === 2) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h1">Set Permission</Typography>
          <div className={classes.stepperWrapper}>
            {!state.isEditForm && (
              <Stepper steps={USER_STEPS} currentStep={2} />
            )}
          </div>
          <div className={classes.buttonsWrapper}>
            <Button
              variant="outlined"
              customVariant="outlinedPrimary"
              onClick={handlePrevStepClick}
              className={classes.backButton}
            >
              {state.isEditForm ? 'Back to Users' : 'Back to Add User'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              customColor="primary"
              onClick={() => createOrUpdateUser()}
            >
              {state.isEditForm ? 'Save' : 'Create User'}
            </Button>
          </div>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card header={<Typography variant="h3">Permissions</Typography>}>
              <Typography>
                Consider who in your team should edit pages, respond to reviews
                or be notified. You can choose if some team members only receive
                notifications of positive or negative reviews to enable you to
                quickly respond to issues or share the good news.
              </Typography>
              <PermissionsTable
                permissions={state?.user?.permissions}
                setPermissions={setPermissions}
              />
            </Card>
          </Grid>
        </Grid>
        <Snackbar
          open={snackbarVisible?.open}
          onClose={handleSnackbarClose}
          alertMessage={snackbarVisible?.message}
          status={snackbarVisible?.status}
        />
      </div>
    )
  }

  if (state.step === 3) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h1">Confirmation</Typography>
          <div className={classes.stepperWrapper}>
            <Stepper steps={USER_STEPS} currentStep={3} />
          </div>
          <div className={classes.buttonsWrapper}>
            <Button
              variant="outlined"
              customVariant="outlinedPrimary"
              onClick={handleBackToUsersClick}
              className={classes.backButton}
            >
              Back to users
            </Button>
          </div>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              header={
                <Typography variant="h3">
                  User {state.isEditForm ? 'updated' : 'created'}
                </Typography>
              }
            >
              <Typography>
                {state.isEditForm
                  ? `The below user has been updated`
                  : `The below user has been created and an email with details on how
                    to login has been sent. You can edit and change the users
                    details and permissions at anytime via the users page.`}
              </Typography>
              <UserSummary
                first_name={state.user?.first_name}
                last_name={state.user?.last_name}
                email={state.user?.email}
                job_title={state.user?.job_title}
              />
            </Card>
          </Grid>
        </Grid>
      </div>
    )
  }

  return <div />
}

export default UserManager
